// Maps storage-hub's `file_metadata` blob (built by its ImportWizard on upload)
// into the conciliatron reader fields it can auto-fill. The blob round-trips
// back on a pick as `FileMetadata.metadata` and carries the CSV columns (in
// header order, so a column's CSV index is its array position) plus the
// analyzed block grid. Everything here is best-effort: absent/partial metadata
// yields a partial result and the caller keeps its manual defaults.

import type { ColumnKind } from '../config/columnRoles';

export interface FileColumn {
  /** The real CSV header — what the C++ wrapper matches on (never importName). */
  name: string;
  /** Raw CSV column index = position in the file's full header. */
  index: number;
  /** storage-hub column role: numeric | category (x/y/z & not_imported excluded). */
  type: string;
  /** The distinct values storage-hub detected at import over the whole file
   *  (sorted; absent past 100 of them). Lets the algorithm config offer "which
   *  categories to plot". Files imported before #2606 carry values from only
   *  the first 1000 rows and must be re-imported. */
  categories?: string[];
  /** Likely no-data / sentinel values storage-hub detected at import (e.g. "-99",
   *  "N/A"), suggested for the missing-data policy so the user doesn't type them.
   *  Absent for files imported before this. Same shape for any column type. */
  sentinelCandidates?: string[];
}

export interface ReaderFields {
  /** Data columns (numeric|category) — feed the model column pickers. */
  availableColumns: FileColumn[];
  /** Every named, importable column (incl. coordinates) — for X/Y/Z name pickers. */
  allColumns: FileColumn[];
  coordinatesIndexes?: [number, number, number];
  delta?: [number, number, number];
  min?: [number, number, number];
  max?: [number, number, number];
  /** CSV delimiter detected by storage-hub on upload (config.delimiter). */
  delimiter?: string;
}

/**
 * The name storage-hub shows for a file: the name typed in its ImportWizard
 * (`file_metadata.name`), else the uploaded `filename` (Bug 2765). Mirrors
 * storage-hub's `resolveDisplayName` (modules/storage-hub/frontend/src/api/
 * mappers.ts) — the blob is untyped, so only a non-blank string counts.
 */
export function fileDisplayName(
  filename: string | undefined,
  metadata: Record<string, unknown> | undefined
): string | undefined {
  const name = metadata?.name;
  return typeof name === 'string' && name.trim() !== '' ? name.trim() : filename;
}

// storage-hub's data-column vocabulary (source of truth:
// modules/storage-hub/frontend/src/config/index.ts). Text columns are typed
// `category` — NOT `categorical`, which storage-hub never emits.
//
// Keyed by ColumnKind rather than a bare Set, so the two vocabularies can't
// drift: a new kind that isn't listed here fails to compile.
const DATA_TYPES: Record<ColumnKind, true> = { numeric: true, category: true };

/** Narrows a raw metadata string to the vocabulary the column pickers filter on.
 *  This is the one border where an untyped blob becomes a typed column. */
function isDataType(type: string): type is ColumnKind {
  return Object.prototype.hasOwnProperty.call(DATA_TYPES, type);
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Column name → a string-array field from storage-hub's precomputed analysis
 *  (`config._precomputed.columnTypes[name][field]`). Used for both `categories`
 *  and `sentinelCandidates`; empty when the file predates that field. */
function stringArrayByColumn(
  meta: Record<string, unknown> | undefined,
  field: string
): Record<string, string[]> {
  const precomputed = asRecord(asRecord(meta?.config)?._precomputed);
  const columnTypes = asRecord(precomputed?.columnTypes);
  if (!columnTypes) return {};
  const out: Record<string, string[]> = {};
  for (const [name, entry] of Object.entries(columnTypes)) {
    const raw = asRecord(entry)?.[field];
    if (Array.isArray(raw)) {
      const values = raw.filter((v): v is string => typeof v === 'string');
      if (values.length > 0) out[name] = values;
    }
  }
  return out;
}

export function readerFieldsFromMetadata(
  metadata: Record<string, unknown> | undefined
): ReaderFields {
  const meta = asRecord(metadata);
  const rawCols = meta && Array.isArray(meta.columns) ? (meta.columns as unknown[]) : [];
  const cols = rawCols.map((c, index) => {
    const rec = asRecord(c) ?? {};
    return {
      column: typeof rec.column === 'string' ? rec.column : '',
      type: typeof rec.type === 'string' ? rec.type : '',
      index,
    };
  });

  // Per-column string arrays from storage-hub's precomputed analysis
  // (config._precomputed.columnTypes[name].*): distinct categories and detected
  // sentinel candidates. Both consumed the same way, for any column type.
  const catByName = stringArrayByColumn(meta, 'categories');
  const sentinelByName = stringArrayByColumn(meta, 'sentinelCandidates');
  const toCol = (c: { column: string; type: string; index: number }): FileColumn => {
    const categories = catByName[c.column];
    const sentinelCandidates = sentinelByName[c.column];
    return {
      name: c.column,
      index: c.index,
      type: c.type,
      ...(categories ? { categories } : {}),
      ...(sentinelCandidates ? { sentinelCandidates } : {}),
    };
  };

  const out: ReaderFields = {
    // Nameless columns are dropped here too, not just from allColumns: a blank
    // header would render as an empty row in every column picker.
    availableColumns: cols.filter((c) => c.column !== '' && isDataType(c.type)).map(toCol),
    allColumns: cols.filter((c) => c.column !== '' && c.type !== 'not_imported').map(toCol),
  };

  const x = cols.find((c) => c.type === 'x_centre')?.index;
  const y = cols.find((c) => c.type === 'y_centre')?.index;
  const z = cols.find((c) => c.type === 'z_centre')?.index;
  if (x !== undefined && y !== undefined && z !== undefined) {
    out.coordinatesIndexes = [x, y, z];
  }

  const config = asRecord(meta?.config);
  if (config && typeof config.delimiter === 'string' && config.delimiter !== '') {
    out.delimiter = config.delimiter;
  }
  const bs = asRecord(config?.blockSize);
  if (bs) {
    const dx = num(bs.x);
    const dy = num(bs.y);
    const dz = num(bs.z);
    if (dx !== undefined && dy !== undefined && dz !== undefined) out.delta = [dx, dy, dz];
  }
  // config.position is the CENTROID bbox. The C++ ModeloVector validates blocks
  // against the block-CORNER extent, so expand the bbox by ±Δ/2 per axis
  // (requires delta). A centroid bbox passed as-is leaves the lowest block out of
  // range (off-by-Δ/2) — see SPEC/1489 note.
  const pos = asRecord(config?.position);
  if (pos && out.delta) {
    const [dx, dy, dz] = out.delta;
    const minX = num(pos.minX);
    const minY = num(pos.minY);
    const minZ = num(pos.minZ);
    const maxX = num(pos.maxX);
    const maxY = num(pos.maxY);
    const maxZ = num(pos.maxZ);
    if (minX !== undefined && minY !== undefined && minZ !== undefined)
      out.min = [minX - dx / 2, minY - dy / 2, minZ - dz / 2];
    if (maxX !== undefined && maxY !== undefined && maxZ !== undefined)
      out.max = [maxX + dx / 2, maxY + dy / 2, maxZ + dz / 2];
  }

  return out;
}
